import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { HarrisonRepositoryToken } from '../../di/tokens';
import type { CreateHarrisonPromptInput } from '../../domain/entities/harrison.entity';

const MODELS_KEY = ['content', 'harrison', 'models'] as const;
const PROMPTS_KEY = ['content', 'harrison', 'prompts'] as const;

/** Lista fixa no back (cópia da do Harrison): não muda durante a sessão. */
export function useHarrisonModels() {
  const repository = container.resolve(HarrisonRepositoryToken);
  return useQuery({
    queryKey: MODELS_KEY,
    queryFn: () => repository.getModels(),
    staleTime: Infinity,
    retry: false,
  });
}

/** Histórico do prompt. Só busca com o modal aberto. */
export function useHarrisonPrompts(enabled: boolean) {
  const repository = container.resolve(HarrisonRepositoryToken);
  return useQuery({
    queryKey: PROMPTS_KEY,
    queryFn: () => repository.listPrompts(),
    enabled,
    retry: false,
  });
}

export function useCreateHarrisonPrompt() {
  const repository = container.resolve(HarrisonRepositoryToken);
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['content', 'harrison', 'create-prompt'],
    mutationFn: (input: CreateHarrisonPromptInput) =>
      repository.createPrompt(input),
    onSuccess: () => client.invalidateQueries({ queryKey: PROMPTS_KEY }),
  });
}

export function useMakeHarrisonPromptDefault() {
  const repository = container.resolve(HarrisonRepositoryToken);
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['content', 'harrison', 'make-prompt-default'],
    mutationFn: (versionId: string) => repository.makePromptDefault(versionId),
    onSuccess: () => client.invalidateQueries({ queryKey: PROMPTS_KEY }),
  });
}

/**
 * O rascunho do comentário oficial da questão. **Não é `useMutation` de
 * propósito**: desmontar cancela a chamada, e a rede global de erro do
 * `MutationCache` mostraria esse cancelamento como falha ("canceled"). Aqui o
 * cancelamento é silencioso e a falha de verdade volta em `error`.
 *
 * `generate` resolve com o markdown que o back devolveu, sem tratamento, ou
 * `null` quando falhou ou foi cancelado.
 */
export function useHarrisonDraft(questionId: string) {
  const repository = container.resolve(HarrisonRepositoryToken);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Desmontar (sair da tela, fechar o modo edição) cancela a geração.
  useEffect(() => () => abortRef.current?.abort(), []);

  const generate = useCallback(
    async (model: string | undefined): Promise<string | null> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setGenerating(true);
      setError(null);
      try {
        const { markdown } = await repository.draftOfficialComment(
          questionId,
          model,
          controller.signal,
        );
        return controller.signal.aborted ? null : markdown;
      } catch (err) {
        if (!controller.signal.aborted) {
          // Só status e código: o erro do axios leva `config` (headers) e o
          // corpo da resposta, que não vão para o console.
          const response = (
            err as {
              response?: { status?: number; data?: { error?: unknown } };
            }
          )?.response;
          console.error('[content] rascunho do Harrison falhou', {
            questionId,
            status: response?.status,
            error: response?.data?.error,
          });
          setError(err);
        }
        return null;
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setGenerating(false);
        }
      }
    },
    [repository, questionId],
  );

  return { generate, generating, error };
}
